import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Smartphone, Monitor, Laptop, Globe, Trash2,
  Loader2, Check, X, Copy, Lock, Unlock, Clock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecuritySettingsTab() {
  const { user } = useContext(AuthContext);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // MFA setup flow
  const [mfaSetup, setMfaSetup] = useState(null); // {qr_code, secret, backup_codes}
  const [mfaVerifyCode, setMfaVerifyCode] = useState("");
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // MFA disable
  const [disablePassword, setDisablePassword] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [mfa, sess, hist, audit] = await Promise.all([
        axios.get(`${API}/security/mfa/status`),
        axios.get(`${API}/security/sessions`),
        axios.get(`${API}/security/login-history`),
        axios.get(`${API}/infrastructure/audit`).catch(() => ({ data: { events: [] } })),
      ]);
      setMfaStatus(mfa.data);
      setSessions(sess.data.sessions || []);
      setLoginHistory(hist.data.history || []);
      setAuditEvents(audit.data.events || []);
    } catch {}
    finally { setLoading(false); }
  };

  const handleSetupMfa = async () => {
    try {
      const res = await axios.post(`${API}/security/mfa/setup`);
      setMfaSetup(res.data);
    } catch {
      toast.error("Failed to initialize MFA setup");
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaVerifyCode || mfaVerifyCode.length < 6) return;
    setMfaVerifying(true);
    try {
      await axios.post(`${API}/security/mfa/verify`, { code: mfaVerifyCode });
      toast.success("MFA enabled successfully!");
      setShowBackupCodes(true);
      setMfaStatus({ enabled: true, backup_codes_remaining: 10 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!disablePassword) return;
    setDisabling(true);
    try {
      await axios.post(`${API}/security/mfa/disable`, { password: disablePassword });
      toast.success("MFA disabled");
      setMfaStatus({ enabled: false, backup_codes_remaining: 0 });
      setMfaSetup(null);
      setShowDisable(false);
      setDisablePassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password");
    } finally {
      setDisabling(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await axios.post(`${API}/security/sessions/revoke`, { session_id: sessionId });
      toast.success("Session revoked");
      setSessions(s => s.filter(x => x.session_id !== sessionId));
    } catch {
      toast.error("Failed to revoke session");
    }
  };

  const handleRevokeAll = async () => {
    try {
      const res = await axios.post(`${API}/security/sessions/revoke-all`);
      toast.success(res.data.message);
      fetchAll();
    } catch {
      toast.error("Failed to revoke sessions");
    }
  };

  const deviceIcon = (info) => {
    if (!info) return <Globe className="w-5 h-5" />;
    if (info.device === "Mobile") return <Smartphone className="w-5 h-5" />;
    if (info.device === "Tablet") return <Laptop className="w-5 h-5" />;
    return <Monitor className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-settings-tab">
      {/* MFA Section */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-outfit flex items-center gap-3">
                <Shield className="w-5 h-5 text-[var(--qn-primary)]" />
                Multi-Factor Authentication
              </CardTitle>
              <CardDescription className="text-[var(--qn-foreground-muted)] mt-1">
                Add an extra layer of security with TOTP authenticator apps
              </CardDescription>
            </div>
            <Badge className={mfaStatus?.enabled
              ? "bg-emerald-500/15 text-emerald-400"
              : "bg-amber-500/15 text-amber-400"
            } data-testid="mfa-status-badge">
              {mfaStatus?.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* MFA Enabled state */}
          {mfaStatus?.enabled && !showDisable && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                <Check className="w-5 h-5 text-emerald-400" />
                <div>
                  <div className="text-sm font-medium">MFA is active</div>
                  <div className="text-xs text-[var(--qn-foreground-muted)]">
                    {mfaStatus.backup_codes_remaining} backup codes remaining
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" className="text-red-400 border-red-400/30 hover:bg-red-500/10" onClick={() => setShowDisable(true)} data-testid="disable-mfa-btn">
                <Unlock className="w-4 h-4 mr-2" /> Disable MFA
              </Button>
            </div>
          )}

          {/* Disable MFA form */}
          {mfaStatus?.enabled && showDisable && (
            <div className="space-y-3 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                <AlertTriangle className="w-4 h-4" /> Confirm MFA Disable
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)]">Enter your password to disable MFA. This will remove the second factor from your account.</p>
              <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Enter your password" className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="disable-mfa-password" />
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleDisableMfa} disabled={disabling || !disablePassword} data-testid="confirm-disable-mfa">
                  {disabling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable MFA"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowDisable(false); setDisablePassword(""); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* MFA Not Enabled — Setup */}
          {!mfaStatus?.enabled && !mfaSetup && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--qn-foreground-muted)]">
                Protect your account with a time-based one-time password (TOTP). Works with Google Authenticator, Authy, 1Password, and more.
              </p>
              <Button className="btn-primary" onClick={handleSetupMfa} data-testid="setup-mfa-btn">
                <Lock className="w-4 h-4 mr-2" /> Set Up MFA
              </Button>
            </div>
          )}

          {/* MFA Setup QR Code Step */}
          {mfaSetup && !showBackupCodes && !mfaStatus?.enabled && (
            <div className="space-y-4">
              <div className="text-sm font-medium">Step 1: Scan QR Code</div>
              <p className="text-xs text-[var(--qn-foreground-muted)]">
                Scan this QR code with your authenticator app
              </p>
              <div className="flex justify-center py-4">
                <img src={mfaSetup.qr_code} alt="MFA QR Code" className="w-48 h-48 rounded-lg border border-[var(--qn-border)]" data-testid="mfa-qr-code" />
              </div>
              <div className="text-xs text-[var(--qn-foreground-muted)] text-center">
                Or enter manually: <code className="bg-[var(--qn-background)] px-2 py-1 rounded font-mono text-[var(--qn-foreground)]" data-testid="mfa-secret">{mfaSetup.secret}</code>
                <button onClick={() => { navigator.clipboard.writeText(mfaSetup.secret); toast.success("Secret copied"); }} className="ml-2 text-[var(--qn-primary)] hover:underline"><Copy className="w-3 h-3 inline" /></button>
              </div>
              <Separator className="bg-[var(--qn-border)]" />
              <div className="text-sm font-medium">Step 2: Verify Code</div>
              <div className="flex gap-2">
                <Input value={mfaVerifyCode} onChange={e => setMfaVerifyCode(e.target.value)} placeholder="Enter 6-digit code" className="bg-[var(--qn-background)] border-[var(--qn-border)] text-center font-mono text-lg tracking-widest" data-testid="mfa-setup-verify-code" maxLength={6} autoComplete="one-time-code" />
                <Button className="btn-primary" onClick={handleVerifyMfa} disabled={mfaVerifying || mfaVerifyCode.length < 6} data-testid="mfa-setup-verify-btn">
                  {mfaVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                </Button>
              </div>
            </div>
          )}

          {/* Backup Codes */}
          {showBackupCodes && mfaSetup?.backup_codes && (
            <div className="space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" /> Save Your Backup Codes
                </div>
                <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Store these codes in a safe place. Each code can only be used once.</p>
                <div className="grid grid-cols-2 gap-2" data-testid="backup-codes-list">
                  {mfaSetup.backup_codes.map((code, i) => (
                    <code key={i} className="bg-[var(--qn-background)] px-3 py-1.5 rounded text-sm font-mono text-center border border-[var(--qn-border)]">{code}</code>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => { navigator.clipboard.writeText(mfaSetup.backup_codes.join("\n")); toast.success("Backup codes copied"); }} data-testid="copy-backup-codes-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy All Codes
                </Button>
              </div>
              <Button className="btn-primary" onClick={() => { setShowBackupCodes(false); setMfaSetup(null); }} data-testid="mfa-setup-done-btn">Done</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit flex items-center gap-3">
              <Monitor className="w-5 h-5 text-[var(--qn-secondary)]" />
              Active Sessions
            </CardTitle>
            {sessions.length > 1 && (
              <Button variant="outline" size="sm" onClick={handleRevokeAll} data-testid="revoke-all-sessions-btn">
                Revoke All Others
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sessions.length > 0 ? (
            <div className="space-y-3" data-testid="sessions-list">
              {sessions.map((s) => (
                <div key={s.session_id} className="flex items-center gap-4 bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
                  <div className="text-[var(--qn-foreground-muted)]">{deviceIcon(s.device_info)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {s.device_info?.browser || "Unknown"} on {s.device_info?.os || "Unknown"}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--qn-foreground-muted)]">
                      <span>{s.ip || "Unknown IP"}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(s.last_active).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:bg-red-500/10" onClick={() => handleRevokeSession(s.session_id)} data-testid={`revoke-session-${s.session_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]">No active sessions found.</div>
          )}
        </CardContent>
      </Card>

      {/* Login History */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Clock className="w-5 h-5 text-[var(--qn-foreground-muted)]" />
            Login History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loginHistory.length > 0 ? (
            <div className="space-y-2" data-testid="login-history-list">
              {loginHistory.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--qn-border)]/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${h.result === "success" || h.action === "user_login" ? "bg-emerald-400" : "bg-red-400"}`} />
                    <div>
                      <div className="text-sm">{h.action?.replace(/_/g, " ")}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)]">{h.ipAddress || "Unknown IP"}</div>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--qn-foreground-muted)]">{new Date(h.timestamp).toLocaleString()}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]">No login history available yet.</div>
          )}
        </CardContent>
      </Card>

      {/* API Call Audit Trail — SOC 2 Type II */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="api-audit-trail">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Shield className="w-5 h-5 text-[var(--qn-foreground-muted)]" />
            API Call Audit Trail
          </CardTitle>
          <CardDescription>All API calls are logged for SOC 2 Type II compliance. Logs retained for 90 days.</CardDescription>
        </CardHeader>
        <CardContent>
          {auditEvents.length > 0 ? (
            <div className="space-y-2" data-testid="audit-events-list">
              {auditEvents.slice(0, 20).map((evt, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--qn-border)]/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${evt.severity === "critical" ? "bg-red-400" : evt.severity === "warning" ? "bg-amber-400" : "bg-emerald-400"}`} />
                    <div>
                      <div className="text-sm">{evt.event_type?.replace(/_/g, " ")}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)]">{evt.action || evt.resource_type || "—"}</div>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--qn-foreground-muted)]">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : "—"}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-[var(--qn-foreground-muted)]" data-testid="audit-empty">
              No API calls recorded yet. Make your first optimization to see the audit trail.
            </div>
          )}

          {/* Compliance indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="compliance-indicators">
            <div className="rounded-lg border border-[var(--qn-border)] p-3">
              <div className="text-xs text-[var(--qn-foreground-subtle)]">Data Encryption</div>
              <div className="text-sm font-medium text-emerald-400 flex items-center gap-1.5 mt-1">
                <Lock className="w-3.5 h-3.5" /> AES-256 at rest, TLS 1.3 in transit
              </div>
            </div>
            <div className="rounded-lg border border-[var(--qn-border)] p-3">
              <div className="text-xs text-[var(--qn-foreground-subtle)]">Rate Limiting</div>
              <div className="text-sm font-medium text-[var(--qn-foreground)] mt-1">1,000 req/min (Free) · 10,000 req/min (Pro)</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
